import { Component, Inject } from '@angular/core';
import { lastValueFrom, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import { RmsPulldownRef } from '../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { PaymentsDataService, PaymentOptions } from '../../../services/payments-data.service';

// =====================================================================
// PartialPaymentComponent — "%" on a row (legacy "Set Partial Payment Amount")
// ---------------------------------------------------------------------
// Pay Method + Bank + Amount (+ deposit date).
//   Post Payment   → records the payment for this order AND posts it (legacy behaviour)
//   Process only   → records it as Processing; post it later with the row's Post button
// Amount can't be more than what's left on the order (the proc re-checks).
// =====================================================================

interface PartialData {
  OrderID: number;
  CompanyName: string;
  LeftToProcess: number;
  BankID: number | null;
  PaymentDetailTypeID: number | null;
  Options: PaymentOptions | null;
}

@Component({
  selector: 'app-partial-payment',
  imports: [RmsModule],
  templateUrl: './partial-payment.component.html',
  styleUrl: './partial-payment.component.scss'
})
export class PartialPaymentComponent {
  Banks: { BankID: number; BankName: string }[] = [];
  Methods: { PaymentDetailTypeID: number; DetailTypeName: string }[] = [];

  BankID: number | null;
  PaymentDetailTypeID: number | null;
  Amount: number | null;
  DepositDate: string = PaymentsDataService.today();
  LeftToProcess: number;

  Saving = false;
  Error = '';

  constructor(
    @Inject(RMS_PULLDOWN_DATA) public data: PartialData,
    public ref: RmsPulldownRef,
    private paymentsData: PaymentsDataService,
    private msg: RmsMessageService
  ) {
    this.Banks = (data.Options?.Banks ?? []).filter(b => b.IsActive);
    // money received only — no store-credit / adjustment types (they never post, so they would land in Exceptions)
    this.Methods = (data.Options?.DetailTypes ?? []).filter(d => d.IsActive && !d.IsWithdrawal && d.PostsToLedger);
    this.BankID = data.BankID ?? this.Banks[0]?.BankID ?? null;
    this.PaymentDetailTypeID = data.PaymentDetailTypeID ?? null;
    this.LeftToProcess = Math.max(0, Number(data.LeftToProcess) || 0);
    this.Amount = null;
  }

  get amountError(): string {
    const a = Number(this.Amount);
    if (this.Amount === null || this.Amount === undefined || String(this.Amount) === '') return '';
    if (!(a > 0)) return 'Amount must be greater than 0.';
    if (a > this.LeftToProcess + 0.005) return `Only ${this.LeftToProcess.toFixed(2)} is left on this order.`;
    return '';
  }

  get isValid(): boolean {
    return !!this.BankID && !!this.PaymentDetailTypeID && Number(this.Amount) > 0 && !this.amountError
      && (!!this.DepositDate);
  }

  async save(postNow: boolean) {
    if (!this.isValid || this.Saving) return;
    this.Saving = true;
    this.Error = '';
    try {
      const result = await lastValueFrom(this.paymentsData.processOrder({
        OrderID: this.data.OrderID,
        BankID: this.BankID!,
        PaymentDetailTypeID: this.PaymentDetailTypeID!,
        Amount: Math.round(Number(this.Amount) * 100) / 100,
        PostNow: postNow,
        DepositDate: postNow ? this.DepositDate : null
      }));
      this.msg.success(postNow
        ? `Partial payment posted on order ${this.data.OrderID}.`
        : `Partial payment processed on order ${this.data.OrderID}.`);
      this.ref.close(result);
    } catch (err: any) {
      this.Error = err?.error?.error ?? 'The payment could not be saved.';
    } finally {
      this.Saving = false;
    }
  }

  cancel() {
    this.ref.close();
  }
}
